import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { AppService } from '../../../store/app.service';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { ReportsApiService } from '../../reports/reports.apiservice';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Router } from '@angular/router';

import { PAGE_NAMES } from '../../../core/helpers/constants';
import { createEmptyGuid } from '../../../core/helpers/utility';
import { ICompany } from '../../../core/models/datafeed.models';
import { ICampaign } from '../../../core/models/campaigns.model';
import { IRollupReportDataResponse } from '../../../core/models/reports.models';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { IPermission } from '../../../core/models/auth.models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-manager-dash',
  templateUrl: './manager-dash.component.html',
  styleUrls: ['./manager-dash.component.scss']
})
export class ManagerDashComponent implements OnInit, OnDestroy {
  @Input() externalUser: boolean = false;
  @Input() iReportFormat: string = '';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  doWhat: IPermission = {} as IPermission;

  // UI state
  active: number = 1;
  loadCompleted = false;
  dataLoadCompleted = false;

  // Data
  platforms: Array<string> = [];
  companiesData: Array<ICompany> = [];
  campValues: Array<{ state: string; count: number }> = [];

  tooManyCompanies = false;
  rollupReportDataResponse: IRollupReportDataResponse | null = null;
  reportformsubmitted = false;
  companyId = '';
  companyData = {} as ICompany;
  campaignsData = new Array<ICampaign>();
  reportPeriods: Array<IReportingPeriod> = [];

  totalLeadCount: number = 0;
  totalClientCount: number = 0;
  totalCampaignCount: number = 0;
  totalActiveCampaignCount: number = 0;
  totalFacebookCampaignCount: number = 0;
  totalGoogleCampaignCount: number = 0;

  interval: any;

  @ViewChild('rollupIframe') rollupIframe?: ElementRef<HTMLIFrameElement>;

  reportForm: FormGroup = this.fb.group({
    key: [null, [Validators.required]],
    startDate: [null, [Validators.required]],
    stopDate: [null, [Validators.required]],
    platforms: ['All', [Validators.required]],
    reportFormat: ['html', [Validators.required]],
    aggregation: ['All', [Validators.required]]
  });

  constructor(
    private appService: AppService,
    private dataFeedService: DataFeedService,
    private reportsService: ReportsApiService,
    private fb: FormBuilder,
    private router: Router,
    private ngxService: NgxUiLoaderService,
    private translate: TranslateService
  ) {}

  get selectedReportPeriod(): string | undefined | null {
    return this.reportForm?.controls?.key?.value;
  }

  get datesReadonly(): boolean {
    return !(this.selectedReportPeriod == 'Custom');
  }

  get completed(): Array<ICampaign> {
    return this.rollupReportDataResponse?.campaigns.filter(
      (f) => f.bundle == 'Faulted' || f.bundle == 'Completed'
    ) ?? [];
  }

  get totalCompleted() {
    return this.completed.length;
  }

  get totalCampaigns() {
    return this.rollupReportDataResponse?.campaigns
      ? this.rollupReportDataResponse?.campaigns?.length
      : 0;
  }

  get combinedStatus() {
    return this.totalCampaigns == 0 ? 0 : (100 * this.totalCompleted) / this.totalCampaigns;
  }

  get isEmptyCompanyGuid() {
    return this.companyId == createEmptyGuid();
  }

  get aggregation() {
    return this.reportForm.get('aggregation') as FormControl;
  }

  get formPlatforms() {
    return this.reportForm.get('platforms') as FormControl;
  }

  get startDate() {
    return this.reportForm.get('startDate') as FormControl;
  }

  get stopDate() {
    return this.reportForm.get('stopDate') as FormControl;
  }

  get reportFormat() {
    return this.reportForm.get('reportFormat') as FormControl;
  }

  get key() {
    return this.reportForm.get('key') as FormControl;
  }

  ngOnInit(): void {
    // When the tab is shown, the component is created; load data once.
    this.viewAllMMDash();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  onFormSubmit() {
    this.ngxService.start();
    this.reportformsubmitted = true;

    if (this.reportForm.invalid) {
      return;
    }

    const campaigns = [createEmptyGuid()];

    const params = {
      companyId: this.companyId,
      reportFormat: this.reportFormat.value,
      campaigns: campaigns,
      aggregation: this.aggregation.value,
      platforms: this.formPlatforms.value,
      dateStart: this.startDate.value,
      dateStop: this.stopDate.value
    };

    let paramsUrl = '' + this.router.createUrlTree([''], { queryParams: params });
    paramsUrl = paramsUrl.replace('/', '');

    this.rollupReportDataResponse = null;
    this.reportsService.startRollupReportProcess(paramsUrl).subscribe((res) => {
      this.rollupReportDataResponse = res.data;
      if (this.rollupReportDataResponse?.orderId) {
        this.dataLoadCompleted = false;
        this.active = 2;

        this.interval = setInterval(() => {
          this.reportsService
            .getRollupReportProgress(this.rollupReportDataResponse?.orderId as string)
            .subscribe((progressRes) => {
              if (progressRes.data.status == 'Completed') {
                clearInterval(this.interval);
                this.dataLoadCompleted = true;
                this.active = 3;

                if (this.rollupReportDataResponse) {
                  this.reportsService
                    .getRollupReport(this.rollupReportDataResponse?.orderId, this.iReportFormat)
                    .subscribe((responseUrl) => {
                      const iframe = this.rollupIframe?.nativeElement;
                      if (iframe) {
                        iframe.src = responseUrl as unknown as string;
                      }
                    });

                  this.loadCompleted = true;
                }
              }
            });

          this.reportsService
            .getRollupReportCampaignsProgress(this.rollupReportDataResponse?.orderId as string)
            .subscribe((campaignsRes) => {
              for (const initial of campaignsRes.data) {
                const campaign = this.rollupReportDataResponse?.campaigns.find(
                  (f) => f.id == initial.campaignId
                );
                if (campaign) {
                  if (initial.status == 'Faulted' || initial.status == 'Completed') {
                    campaign.bundle = initial.status;
                    campaign.opportunityNotes = initial.message;
                  } else if (
                    initial.status == 'Started' &&
                    !(campaign.bundle == 'Faulted' || campaign.bundle == 'Completed')
                  ) {
                    campaign.bundle = initial.status;
                    campaign.opportunityNotes = initial.message;
                  }
                }
              }

              if (this.rollupReportDataResponse) {
                this.rollupReportDataResponse.campaigns = this.rollupReportDataResponse.campaigns.sort(
                  (a, b) => (a.bundle == 'Started' && b.bundle !== 'Started' ? -1 : 1)
                );
              }
            });
        }, 1000);
      }
    });
  }

  onReportPeriodChange(selectedPeriod: string) {
    const period = this.reportPeriods.find((f) => f.key == selectedPeriod);

    this.key.setValue(selectedPeriod);
    this.startDate.setValue(period?.startDate);
    this.stopDate.setValue(period?.stopDate);

    this.active = 1;
  }

  getReportingPeriodTitle(key?: string): string {
    const code = (key ?? '')
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();
    if (!code) {
      return key ?? '';
    }

    const translationKey = `REPORTING_PERIOD.${code}`;
    const translated = this.translate.instant(translationKey);
    return translated && translated !== translationKey ? translated : key ?? '';
  }

  handlePlatformsChange($event: any, _val: string) {
    const target = $event.target;
    if (target.checked) {
      this.active = 1;
    }
  }

  onFrameLoadComplete() {
    if (this.rollupReportDataResponse) {
      this.loadCompleted = true;
    }
    this.ngxService.stopAll();
  }

  private viewAllMMDash() {
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Campaigns);
    this.appService
      .getAllCampaigns$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.campaignsData = [...response.data];
        this.totalCampaignCount = response.totalCount;

        const totalActiveCampaigns = [...response.data].filter((x: any) => x.stage !== 'Inactive');
        this.totalActiveCampaignCount = totalActiveCampaigns.length;
        this.totalFacebookCampaignCount = totalActiveCampaigns.filter(
          (x: any) => x.platform === 'Facebook'
        ).length;
        this.totalGoogleCampaignCount = totalActiveCampaigns.filter((x: any) => x.platform === 'Google').length;

        const counts = [...response.data].reduce((acc: { [key: string]: number }, item) => {
          let key = item['stageId']?.toString();
          if (key) {
            key = DASH_CAMPAIGN_STAGES.find((x) => x.value === Number(key))?.titleKey as unknown as string;
            if (key !== 'INACTIVE') {
              acc[key] = (acc[key] || 0) + 1;
            }
          }
          return acc;
        }, {});

        this.campValues = Object.entries(counts)
          .map(([state, count]) => ({ state, count: Number(count) }))
          .sort(
            (a, b) =>
              CALLING_DASH_CAMPAIGN_STAGES.indexOf(a.state) -
              CALLING_DASH_CAMPAIGN_STAGES.indexOf(b.state)
          );
      });
    this.appService.fetchAllCampaigns({});

    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Clients);
    this.appService
      .getAllClients$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.totalClientCount = [...response.data].length;
      });
    this.appService.fetchAllClients({});

    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Leads);
    this.appService
      .getAllLeads$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.leads) {
          this.totalLeadCount = response.totalCount;
        }
      });
    this.appService.fetchAllLeads({});

    this.dataFeedService.getAssociatedCompanies().subscribe((res) => {
      this.companiesData = [...res.data];
      if ([...res.data].length > 1) {
        this.tooManyCompanies = true;
      } else {
        this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Reports);
        const id = [...res.data][0]['id'];

        if (id) {
          this.companyId = id;
          if (!this.isEmptyCompanyGuid) {
            this.dataFeedService
              .getCompanyData(id)
              .pipe(takeUntil(this.destroyed$))
              .subscribe((response: any) => {
                this.companyData = response.data;
              });
          }

          this.dataFeedService
            .getCompanyAssociatedCampaigns(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response: any) => {
              this.platforms = Array.from(
                new Set(
                  [...response.data]
                    .filter((f) => f.campaignAdAccounts.length > 0)
                    .map((item) => item.platform)
                )
              );
            });
        }

        this.reportsService
          .getSupportedReportingPeriods()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: Array<any>) => {
            this.reportPeriods = response as Array<IReportingPeriod>;
            this.reportPeriods = this.reportPeriods.slice(1);
            this.onReportPeriodChange('Past Week');
          });
      }
    });
  }
}

export const DASH_CAMPAIGN_STAGES = [
  {
    titleKey: 'TECH_SETUP',
    value: 1
  },
  {
    titleKey: 'CAMPAIGN_SET_UP',
    value: 2
  },
  {
    titleKey: 'CLIENT_APPROVAL',
    value: 3
  },
  {
    titleKey: 'QC',
    value: 4
  },
  {
    titleKey: 'WAITING_TO_START',
    value: 5
  },
  {
    titleKey: 'ACTIVE',
    value: 6
  },
  {
    titleKey: 'PAUSED',
    value: 7
  },
  {
    titleKey: 'INACTIVE',
    value: 8
  },
  {
    titleKey: 'CANCELLING',
    value: 9
  }
];

export const CALLING_DASH_CAMPAIGN_STAGES = [
  'TECH_SETUP',
  'CAMPAIGN_SET_UP',
  'CLIENT_APPROVAL',
  'QC',
  'WAITING_TO_START',
  'ACTIVE',
  'CANCELLING',
  'PAUSED'
];
